import React, { useState, useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Tooltip,
  useTheme
} from '@material-ui/core';
import {
  Security,
  Warning,
  Person,
  Assessment,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  ErrorOutline,
  HelpOutline,
  InfoOutlined
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { Chart } from 'react-google-charts';

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    background: 'linear-gradient(135deg, #061739 0%, #0055A2 50%, #428BC9 100%)',
    color: 'white',
    borderRadius: 16,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      width: '300px',
      height: '300px',
      background: 'rgba(255,255,255,0.1)',
      borderRadius: '50%',
      transform: 'translate(50%, -50%)',
    },
  },
  heroContent: {
    position: 'relative',
    zIndex: 1,
  },
  analyticsGrid: {
    marginTop: theme.spacing(3),
  },
  kpiCard: {
    borderRadius: 12,
    height: '100%',
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.2)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 30px rgba(0,0,0,0.15)',
    },
  },
  kpiIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  userTable: {
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.95)',
    borderRadius: 12,
    marginTop: theme.spacing(3),
  },
  riskChip: {
    fontWeight: 600,
    fontSize: '0.75rem',
  },
  chartContainer: {
    height: 300,
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.95)',
    borderRadius: 12,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  tabContainer: {
    borderBottom: '1px solid rgba(255,255,255,0.3)',
    marginBottom: theme.spacing(2),
    '& .MuiTabs-indicator': {
      backgroundColor: 'white',
    },
    '& .MuiTab-root': {
      color: 'rgba(255,255,255,0.7)',
      '&.Mui-selected': {
        color: 'white',
      },
    },
  },
  summaryBox: {
    background: 'rgba(255,255,255,0.1)',
    borderRadius: 12,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    border: '1px solid rgba(255,255,255,0.2)',
  },
  warningBox: {
    background: 'rgba(66, 139, 201, 0.1)',
    border: '1px solid rgba(66, 139, 201, 0.3)',
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  }
}));

const RealityBasedPhishingSafety = ({ userOverviewData, dateRange = '30days', getTranslation = (text) => text, preferredLanguage = 'EN' }) => {
  const classes = useStyles();
  const theme = useTheme();
  
  // Map global dateRange to period index
  const getPeriodIndex = () => {
    switch (dateRange) {
      case 'today': return 0;
      case '7days': return 1;
      case '30days': return 2;
      default: return 2; // Default to 30 days
    }
  };
  
  const selectedPeriod = getPeriodIndex();

  // Get period label for display
  const getPeriodLabel = () => {
    switch (dateRange) {
      case 'today': return getTranslation('Today', preferredLanguage);
      case '7days': return getTranslation('7 Days', preferredLanguage);
      case '30days': return getTranslation('30 Days', preferredLanguage);
      default: return getTranslation('30 Days', preferredLanguage);
    }
  };
  
  // Process user overview data based on global dateRange
  const processedData = useMemo(() => {
    if (!userOverviewData) return null;

    const periods = ['today', 'sevenDays', 'thirtyDays'];
    const currentPeriod = periods[selectedPeriod];
    const periodData = userOverviewData[currentPeriod] || [];

    // Safe-parse a percentage field that may be a string like "58.20%" or a number
    const safeParsePct = (val) => {
      if (typeof val === 'number') return val;
      if (typeof val === 'string') return parseFloat(val.replace('%', '')) || 0;
      return 0;
    };

    // Filter out rows missing required keys or with zero clicks
    const validUsers = periodData.filter(user =>
      user && typeof user === 'object' && typeof user.email === 'string'
    );
    const activeUsers = validUsers.filter(user => (Number(user.clicks) || 0) > 0);
    
    // Calculate company-wide metrics
    const totalClicks = validUsers.reduce((sum, user) => sum + (Number(user.clicks) || 0), 0);
    const totalUsers = validUsers.length;
    const activeUserCount = activeUsers.length;

    // Calculate weighted averages for percentages
    let totalUnsafeClicks = 0;
    let totalUnknownClicks = 0;

    activeUsers.forEach(user => {
      const clicks = Number(user.clicks) || 0;
      const unsafePercent = safeParsePct(user.unSafePercent);
      const unknownPercent = safeParsePct(user.unknownPercent);
      
      totalUnsafeClicks += (clicks * unsafePercent / 100);
      totalUnknownClicks += (clicks * unknownPercent / 100);
    });

    const avgUnsafePercent = totalClicks > 0 ? (totalUnsafeClicks / totalClicks * 100) : 0;
    const avgUnknownPercent = totalClicks > 0 ? (totalUnknownClicks / totalClicks * 100) : 0;
    const safePercent = 100 - avgUnsafePercent - avgUnknownPercent;

    // Risk assessment
    const getRiskLevel = (unsafePercent, unknownPercent) => {
      const totalRisk = unsafePercent + unknownPercent;
      if (totalRisk >= 70) return 'high';
      if (totalRisk >= 40) return 'medium';
      if (totalRisk >= 20) return 'low';
      return 'minimal';
    };

    const companyRiskLevel = getRiskLevel(avgUnsafePercent, avgUnknownPercent);

    // Sort users by risk (unsafe + unknown percentages)
    const sortedUsers = activeUsers.map(user => {
      const unsafePercent = safeParsePct(user.unSafePercent);
      const unknownPercent = safeParsePct(user.unknownPercent);
      const totalRiskPercent = unsafePercent + unknownPercent;
      const riskLevel = getRiskLevel(unsafePercent, unknownPercent);
      const email = typeof user.email === 'string' ? user.email : 'unknown';
      
      return {
        ...user,
        unsafePercent,
        unknownPercent,
        totalRiskPercent,
        riskLevel,
        safePercent: 100 - totalRiskPercent,
        username: email.split('@')[0] + '@'
      };
    }).sort((a, b) => b.totalRiskPercent - a.totalRiskPercent);

    return {
      totalClicks,
      totalUsers,
      activeUserCount,
      avgUnsafePercent,
      avgUnknownPercent,
      safePercent,
      companyRiskLevel,
      sortedUsers,
      periodLabel: currentPeriod === 'today' ? 'Today' : currentPeriod === 'sevenDays' ? '7 Days' : '30 Days'
    };
  }, [userOverviewData, dateRange, selectedPeriod]);

  // Chart data for pie chart
  const chartData = useMemo(() => {
    if (!processedData) return null;

    return [
      ['Link Safety Category', 'Percentage of Clicks'],
      ['Safe Links', processedData.safePercent],
      ['Unknown Links', processedData.avgUnknownPercent],
      ['Unsafe Links', processedData.avgUnsafePercent]
    ];
  }, [processedData]);

  // Chart options
  const chartOptions = {
    backgroundColor: 'transparent',
    chartArea: { left: 20, top: 20, width: '90%', height: '85%' },
    colors: ['#4caf50', '#ffc107', '#f44336'], // Safety colors: Safe=Green, Unknown=Gold, Unsafe=Red
    legend: {
      position: 'right',
      alignment: 'center',
      textStyle: {
        color: theme.palette.text.primary,
        fontSize: 12
      }
    },
    pieSliceText: 'percentage',
    pieSliceTextStyle: {
      color: 'white',
      fontSize: 14,
      fontWeight: 'bold'
    },
    tooltip: {
      textStyle: { fontSize: 12 },
      showColorCode: true
    }
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case 'high': return '#f44336';      // Red for high risk
      case 'medium': return '#ffc107';    // Gold for medium risk
      case 'low': return '#ff9800';       // Orange for low risk
      case 'minimal': return '#4caf50';   // Green for minimal risk
      default: return '#9e9e9e';
    }
  };

  const getRiskIcon = (riskLevel) => {
    switch (riskLevel) {
      case 'high': return <ErrorOutline />;
      case 'medium': return <Warning />;
      case 'low': return <HelpOutline />;
      case 'minimal': return <CheckCircle />;
      default: return <InfoOutlined />;
    }
  };

  if (!userOverviewData) {
    return (
      <Paper className={classes.mainContainer}>
        <Box className={classes.heroContent}>
          <Typography variant="h5" style={{ fontWeight: 600, marginBottom: 8 }}>
            {getTranslation('Reality Based Phishing Safety', preferredLanguage)}
          </Typography>
          <Typography variant="body1" style={{ opacity: 0.9 }}>
            {getTranslation('Real-world phishing safety data not available', preferredLanguage)}
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper className={classes.mainContainer}>
      <Box className={classes.heroContent}>
        {/* Header Section */}
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <Security style={{ fontSize: 48, opacity: 0.9 }} />
          </Grid>
          <Grid item xs>
            <Typography variant="h4" style={{ fontWeight: 700, marginBottom: 4 }}>
              {getTranslation('Reality Based Phishing Safety', preferredLanguage)}
            </Typography>
            <Typography variant="h6" style={{ opacity: 0.9, fontWeight: 400 }}>
              {getTranslation('Real-world phishing encounters', preferredLanguage)} • {getPeriodLabel()} • {getTranslation('Superior to traditional simulations', preferredLanguage)}
            </Typography>
          </Grid>
          <Grid item>
            <Tooltip title={getTranslation('Based on actual phishing threats your users encounter daily', preferredLanguage)}>
              <IconButton style={{ color: 'white' }}>
                <InfoOutlined />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>


        {processedData && (
          <>
            {/* Executive Summary */}
            <Box className={classes.summaryBox}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="h3" style={{ fontWeight: 700, marginBottom: 4 }}>
                    {processedData.totalClicks.toLocaleString()}
                  </Typography>
                  <Typography variant="body1" style={{ opacity: 0.9 }}>
                    {getTranslation('Total Real Clicks', preferredLanguage)} ({getPeriodLabel()})
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="h3" style={{ fontWeight: 700, marginBottom: 4, color: '#E9F2F9' }}>
                    {Math.round(processedData.safePercent)}%
                  </Typography>
                  <Typography variant="body1" style={{ opacity: 0.9 }}>
                    {getTranslation('Safe Link Clicks', preferredLanguage)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="h3" style={{ fontWeight: 700, marginBottom: 4, color: '#E9F2F9' }}>
                    {Math.round(processedData.avgUnknownPercent)}%
                  </Typography>
                  <Typography variant="body1" style={{ opacity: 0.9 }}>
                    {getTranslation('Unknown Risk Clicks', preferredLanguage)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="h3" style={{ fontWeight: 700, marginBottom: 4, color: '#E9F2F9' }}>
                    {Math.round(processedData.avgUnsafePercent)}%
                  </Typography>
                  <Typography variant="body1" style={{ opacity: 0.9 }}>
                    {getTranslation('Unsafe Link Clicks', preferredLanguage)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Analytics Grid */}
            <Grid container spacing={3} className={classes.analyticsGrid}>
              {/* Safety Distribution Chart */}
              <Grid item xs={12} md={6}>
                <Card className={classes.kpiCard} style={{ height: 370 }}>
                  <CardContent style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 16, color: theme.palette.text.primary }}>
                      {getTranslation('Real-World Click Safety Distribution', preferredLanguage)}
                    </Typography>
                    <Box style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {chartData && (
                        <Chart
                          chartType="PieChart"
                          data={chartData}
                          options={chartOptions}
                          width="100%"
                          height="280px"
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Company Risk Assessment */}
              <Grid item xs={12} md={6}>
                <Card className={classes.kpiCard} style={{ height: 370 }}>
                  <CardContent style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box className={classes.kpiIcon} style={{ 
                      backgroundColor: `${getRiskColor(processedData.companyRiskLevel)}20`, 
                      color: getRiskColor(processedData.companyRiskLevel) 
                    }}>
                      {getRiskIcon(processedData.companyRiskLevel)}
                    </Box>
                    
                    <Typography variant="h4" style={{ 
                      fontWeight: 700, 
                      color: getRiskColor(processedData.companyRiskLevel),
                      textTransform: 'uppercase',
                      marginBottom: 8
                    }}>
                      {getTranslation(processedData.companyRiskLevel, preferredLanguage)} {getTranslation('Risk', preferredLanguage)}
                    </Typography>
                    
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                      {getTranslation('Company-wide phishing safety assessment', preferredLanguage)}
                    </Typography>
                    
                    <Box style={{ marginBottom: 16, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography variant="body2" style={{ marginBottom: 8 }}>
                        {getTranslation('Overall Safety Score', preferredLanguage)}
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={processedData.safePercent} 
                        style={{ height: 12, borderRadius: 6, backgroundColor: '#e0e0e0', marginBottom: 8 }}
                      />
                      <Typography variant="body1" style={{ marginBottom: 16, fontWeight: 600 }}>
                        {Math.round(processedData.safePercent)}% {getTranslation('of clicks on safe links', preferredLanguage)}
                      </Typography>

                      <Typography variant="body2" color="textSecondary">
                        <strong>{getTranslation('Active Users', preferredLanguage)}:</strong> {processedData.activeUserCount} {getTranslation('of', preferredLanguage)} {processedData.totalUsers} {getTranslation('users clicked links', preferredLanguage)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* User Risk Analysis Table */}
            <TableContainer component={Paper} className={classes.userTable}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>{getTranslation('User', preferredLanguage)}</strong></TableCell>
                    <TableCell align="center"><strong>{getTranslation('Total Clicks', preferredLanguage)}</strong></TableCell>
                    <TableCell align="center"><strong>{getTranslation('Safe %', preferredLanguage)}</strong></TableCell>
                    <TableCell align="center"><strong>{getTranslation('Unknown %', preferredLanguage)}</strong></TableCell>
                    <TableCell align="center"><strong>{getTranslation('Unsafe %', preferredLanguage)}</strong></TableCell>
                    <TableCell align="center"><strong>{getTranslation('Risk Level', preferredLanguage)}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {processedData.sortedUsers.map((user, index) => (
                    <TableRow key={user.email} style={{ backgroundColor: index < 3 ? 'rgba(255, 193, 7, 0.1)' : 'transparent' }}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar style={{ width: 32, height: 32, fontSize: 14 }}>
                            {user.username[0].toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                              {user.username}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          {user.clicks.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" style={{ color: '#4caf50', fontWeight: 600 }}>
                          {Math.round(user.safePercent)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" style={{ color: '#ffc107', fontWeight: 600 }}>
                          {Math.round(user.unknownPercent)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" style={{ color: '#f44336', fontWeight: 600 }}>
                          {Math.round(user.unsafePercent)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={user.riskLevel.toUpperCase()}
                          className={classes.riskChip}
                          style={{
                            backgroundColor: `${getRiskColor(user.riskLevel)}20`,
                            color: getRiskColor(user.riskLevel)
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Educational Note */}
            <Box className={classes.warningBox}>
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Assessment style={{ color: '#428BC9', marginTop: 4 }} />
                <Box>
                  <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4, color: '#ffffff' }}>
                    {getTranslation('Reality-Based vs Traditional Phishing Simulations', preferredLanguage)}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#ffffff', lineHeight: 1.5, fontSize: '0.9rem' }}>
                    {getTranslation('This analysis uses real phishing threats your users encounter daily, providing superior insights compared to artificial simulations. Users natural responses to genuine threats offer authentic behavior patterns for security training and risk assessment.', preferredLanguage)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default RealityBasedPhishingSafety;